import React from 'react'
import {
    Box,
    Button,
    ButtonGroup,
    Chip,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import AssessmentIcon from '@material-ui/icons/Assessment'
import CategoryIcon from '@material-ui/icons/Category'
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
    },
    titleBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        minWidth: 0,
        flex: '1 1 280px',
    },
    title: {
        fontWeight: 600,
        letterSpacing: '-0.02em',
    },
    subtitle: {
        maxWidth: 580,
        lineHeight: 1.45,
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
    },
    viewButton: {
        textTransform: 'none',
        fontWeight: 600,
    },
}))

export type CatalogViewMode = 'library' | 'coverage'

interface CatalogWorkspaceHeaderProps {
    title: string
    librarySubtitle: string
    coverageSubtitle: string
    viewMode: CatalogViewMode
    onViewModeChange: (mode: CatalogViewMode) => void
    typesLabel: string
    onOpenTypes: () => void
    typesDisabled?: boolean
    coverageAlertCount?: number
}

export const CatalogWorkspaceHeader: React.FC<CatalogWorkspaceHeaderProps> = ({
    title,
    librarySubtitle,
    coverageSubtitle,
    viewMode,
    onViewModeChange,
    typesLabel,
    onOpenTypes,
    typesDisabled = false,
    coverageAlertCount = 0,
}) => {
    const classes = useStyles()

    return (
        <Box className={classes.root}>
            <Box className={classes.titleBlock}>
                <Typography
                    variant="h5"
                    component="h2"
                    className={classes.title}
                >
                    {title}
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.subtitle}
                >
                    {viewMode === 'coverage'
                        ? coverageSubtitle
                        : librarySubtitle}
                </Typography>
            </Box>
            <Box className={classes.actions}>
                <ButtonGroup color="primary" size="small">
                    <Button
                        className={classes.viewButton}
                        variant={
                            viewMode === 'library' ? 'contained' : 'outlined'
                        }
                        startIcon={<LibraryBooksIcon fontSize="small" />}
                        onClick={() => onViewModeChange('library')}
                    >
                        Library
                    </Button>
                    <Button
                        className={classes.viewButton}
                        variant={
                            viewMode === 'coverage' ? 'contained' : 'outlined'
                        }
                        startIcon={<AssessmentIcon fontSize="small" />}
                        onClick={() => onViewModeChange('coverage')}
                    >
                        Coverage
                    </Button>
                </ButtonGroup>
                {coverageAlertCount > 0 && (
                    <Chip
                        size="small"
                        color="secondary"
                        label={`${coverageAlertCount} required missing`}
                    />
                )}
                <Button
                    size="small"
                    variant="outlined"
                    className={classes.viewButton}
                    startIcon={<CategoryIcon fontSize="small" />}
                    onClick={onOpenTypes}
                    disabled={typesDisabled}
                >
                    {typesLabel}
                </Button>
            </Box>
        </Box>
    )
}
